import type { WithActiveBranchId } from '@app/api/shared';

export enum MedicalDocumentsEndpoints {
  GetMedicalDocumentByRecordId = 'GetMedicalDocumentByRecordId',
  CreateMedicalDocument = 'CreateMedicalDocument',
  GetMedicalDocumentsFilterData = 'GetMedicalDocumentsFilterData',
  UpdateMedicalCard = 'UpdateMedicalCard',
  SearchMedicalDocuments = 'SearchMedicalDocuments',
  GetMedicalDocumentsByClient = 'GetMedicalDocumentsByClient',
  UpsertBranchMedicalExtension = 'UpsertBranchMedicalExtension',
  GetBranchMedicalExtension = 'GetBranchMedicalExtension',
  SendToMedFlex = 'SendToMedFlex',
  GetMedicalDocumentById = 'GetMedicalDocumentById',
  SubmitMedicalDocument = 'SubmitMedicalDocument',
  GetMedicalDocumentXml = 'GetMedicalDocumentXml',
  SignMedicalDocument = 'SignMedicalDocument',
  UpdateMedicalDocumentStatus = 'UpdateMedicalDocumentStatus',
  UpdateMedicalDocumentsStatuses = 'UpdateMedicalDocumentsStatuses',
}

export type SignerSignRole = 'DOCTOR' | 'CHIEF';

export interface MedflexError {
  code: string | null;
  message: string | null;
}

export interface ModelDiagnosis {
  Code: string | null;
  Description: string | null;
  Type: number;
  Nature: number;
}

export interface ModelAnamnesis {
  DiseaseHistory: string | null;
  LifeHistory: string | null;
  AllergyHistory: string | null;
}

export interface ModelBloodPressure {
  Systolic: number;
  Diastolic: number;
}

export interface ModelObjectiveStatus {
  GeneralCondition: number;
  Temperature: number | null;
  BloodPressure: ModelBloodPressure;
  HeartRate: number | null;
  RespiratoryRate: number | null;
  Skin: string | null;
  RespiratorySystem: string | null;
  CardiovascularSystem: string | null;
  DigestiveSystem: string | null;
  LocalStatus: string | null;
}

export interface ModelMedication {
  Name: string | null;
  Dosage: string | null;
  AdministrationRoute: string | null;
  Frequency: string | null;
  Duration: string | null;
}

export interface ModelProcedure {
  Name: string | null;
  Quantity: number | null;
  Note: string | null;
}

export interface ModelTreatment {
  Recommendations: string | null;
  Medications: ModelMedication[] | null;
  Procedures: ModelProcedure[] | null;
  Regimen: number;
  FollowUp: string | null;
}

export interface ModelClinicalData {
  PrimaryDiagnosis: ModelDiagnosis;
  SecondaryDiagnoses: ModelDiagnosis[] | null;
  Complaints: string | null;
  Anamnesis: ModelAnamnesis;
  ObjectiveStatus: ModelObjectiveStatus;
  Treatment: ModelTreatment;
}

export interface ModelPatientDocument {
  Type?: string | null;
  Number?: string | null;
}

export interface ModelPatientSnapshot {
  // Формируется на сервере, с фронта не передаётся.
  LocalId?: string | null;
  Surname: string;
  Name: string;
  Patronymic?: string | null;
  // Формат "yyyy-MM-dd".
  BirthDate: string;
  Snils?: string | null;
  // Код пола по справочнику НСИ 1.2.643.5.1.13.13.11.1040: "1" | "2".
  Gender: string;
  Phone?: string | null;
  Document?: ModelPatientDocument;
  Address?: string | null;
}

export interface ModelDoctorSnapshot {
  LocalId?: string | null;
  Surname: string;
  Name: string;
  Patronymic?: string | null;
  // Формат "yyyy-MM-dd".
  BirthDate?: string | null;
  Snils?: string | null;
  // Роль подписанта: DOCTOR или ASSISTANT.
  Role: string;
  // Коды по справочникам НСИ (1002 «Должности», 1066 «Специальности»).
  PositionCode?: string | null;
  Speciality?: string | null;
  Phone?: string | null;
  Email?: string | null;
}

export interface ModelMedicalDocumentCreate {
  PatientId?: number | null;
  RecordId?: number | null;
  // Код вида документа СЭМД (справочник НСИ 1.2.643.5.1.13.13.11.1520).
  Kind: number;
  ResponsibleDoctorId?: number | null;
  ClinicalData?: ModelClinicalData;
  Patient?: ModelPatientSnapshot;
  Doctor?: ModelDoctorSnapshot;
  // В swagger-схеме Create этого поля уже нет, но текущая форма
  // МедКарты его отправляет — судьба поля решается в части 2 блока.
  DocumentNumber?: string;
}

export interface ModelMedicalDocumentUpdate {
  ResponsibleDoctorId?: number | null;
  ClinicalData?: ModelClinicalData;
  Patient?: ModelPatientSnapshot;
  Doctor?: ModelDoctorSnapshot;
}

export interface ModelMedicalDocumentResponse {
  Id: number;
  OrganizationId: number;
  OrganizationName: string | null;
  BranchId: number | null;
  BranchName: string | null;
  PatientId: number | null;
  PatientName: string | null;
  RecordId: number | null;
  Kind: number;
  KindName: string | null;
  DocumentNumber: string | null;
  LocalDocumentUid: string;
  ResponsibleDoctorId: number | null;
  ResponsibleDoctorName: string | null;
  Status: string | null;
  ClinicalData: ModelClinicalData;
  // Слепки пациента и врача — JSON-строки.
  Patient: string | null;
  Doctor: string | null;
  ProceduresCount: number | null;
  RequestId: string | null;
  EmdrId: string | null;
  RegistrationDateTime: string | null;
  CreationDateTime: string;
  CreatedAt: string;
  UpdatedAt: string;
  DoctorSignedByUserId: number | null;
  DoctorSignedByUserName: string | null;
  DoctorSignedDateTime: string | null;
  DoctorCertificateThumbprint: string | null;
  ChiefSignedByUserId: number | null;
  ChiefSignedByUserName: string | null;
  ChiefSignedDateTime: string | null;
  ChiefCertificateThumbprint: string | null;
  RejectionReason: string | null;
  Errors: MedflexError[] | null;
  SemdXml: string | null;
}

export interface ModelMedicalDocumentDoctorFilterItem {
  DoctorId: number;
  DoctorName: string | null;
}

export interface DateRangeData {
  From: string | null;
  To: string | null;
}

export interface ModelMedicalDocumentFilterData {
  Statuses: string[] | null;
  Doctors: ModelMedicalDocumentDoctorFilterItem[] | null;
  CreationDateFrom: string | null;
  CreationDateTo: string | null;
  DoctorSignedDateRange: DateRangeData;
  ChiefSignedDateRange: DateRangeData;
}

export interface ModelMedicalDocumentListResult {
  Items: ModelMedicalDocumentResponse[] | null;
  Total: number;
  Page: number;
  PageSize: number;
  PageCount: number;
}

export interface ModelBranchMedicalExtension {
  BranchId: number;
  // OID структурного подразделения по справочнику ФРМО.
  FrmoOid: string | null;
}

export interface MedflexRegisterResponse {
  request_id: string;
  detail: string | null;
}

export interface ModelSignRequest {
  Role: SignerSignRole;
  CertificateThumbprint: string;
  // BASE64 CMS-подпись.
  SignatureData: string;
}

export type GetMedicalDocumentByRecordIdRequest = WithActiveBranchId<{
  recordId: number;
}>;

export type CreateMedicalDocumentRequest = WithActiveBranchId<{
  data: ModelMedicalDocumentCreate;
}>;

export type GetMedicalDocumentsFilterDataRequest = WithActiveBranchId;

export type UpdateMedicalCardRequest = WithActiveBranchId<{
  id: number;
  data: ModelMedicalDocumentUpdate;
}>;

export type SearchMedicalDocumentsRequest = WithActiveBranchId<{
  PatientId: number | null;
  DoctorId: number | null;
  Statuses: string[] | null;
  Kind: number;
  IsSigned: boolean;
  SignedByUserId: number | null;
  DateFrom: string | null;
  DateTo: string | null;
  DoctorSignedDateFrom: string | null;
  DoctorSignedDateTo: string | null;
  ChiefSignedDateFrom: string | null;
  ChiefSignedDateTo: string | null;
  SearchTerm: string | null;
  Page: number;
  PageSize: number;
}>;

export type GetMedicalDocumentsByClientRequest = WithActiveBranchId<{
  clientId: number;
}>;

export type UpsertBranchMedicalExtensionRequest = WithActiveBranchId<{
  data: ModelBranchMedicalExtension;
}>;

export type GetBranchMedicalExtensionRequest = WithActiveBranchId<{
  branchId: number;
}>;

export type SendToMedFlexRequest = WithActiveBranchId<{
  id: number;
}>;

export type GetMedicalDocumentByIdRequest = WithActiveBranchId<{
  id: number;
}>;

export type SubmitMedicalDocumentRequest = WithActiveBranchId<{
  id: number;
}>;

export type GetMedicalDocumentXmlRequest = WithActiveBranchId<{
  id: number;
}>;

export type SignMedicalDocumentRequest = WithActiveBranchId<{
  documentId: number;
  data: ModelSignRequest;
}>;

export type UpdateMedicalDocumentStatusRequest = WithActiveBranchId<{
  id: number;
}>;

export type UpdateMedicalDocumentsStatusesRequest = WithActiveBranchId;

export type GetMedicalDocumentByRecordIdResponse =
  ModelMedicalDocumentResponse[];
export type CreateMedicalDocumentResponse = ModelMedicalDocumentResponse;
export type GetMedicalDocumentsFilterDataResponse =
  ModelMedicalDocumentFilterData;
export type UpdateMedicalCardResponse = ModelMedicalDocumentResponse;
export type SearchMedicalDocumentsResponse = ModelMedicalDocumentListResult;
export type GetMedicalDocumentsByClientResponse =
  ModelMedicalDocumentResponse[];
export type UpsertBranchMedicalExtensionResponse = ModelBranchMedicalExtension;
export type GetBranchMedicalExtensionResponse = ModelBranchMedicalExtension;
export type SendToMedFlexResponse = MedflexRegisterResponse;
export type GetMedicalDocumentByIdResponse = ModelMedicalDocumentResponse;
export type SubmitMedicalDocumentResponse = ModelMedicalDocumentResponse;
// XML СЭМД в Base64 для подписания плагином КриптоПро на стороне клиента:
// серверное ГОСТ-хэширование (GetHash) требует лицензионного пакета КриптоПро.
export type GetMedicalDocumentXmlResponse = string;
export type SignMedicalDocumentResponse = ModelMedicalDocumentResponse;
export type UpdateMedicalDocumentStatusResponse = ModelMedicalDocumentResponse;
export type UpdateMedicalDocumentsStatusesResponse = void;
